import { NetworkStatus, useQuery } from '@apollo/client';

import { paginateData } from '~/lib/graphql/pagination';

import query from './query.graphql';

const INITIAL_PAGE_SIZE = 40;
const PAGE_SIZE = 20;

type CompanyQueryVariables = {
  first: number;
  id: string;
  type?: string;
  excludingAnonymous?: boolean;
  path?: string;
};
type UseCompanyEventArguments = {
  company: any;
  type?: string;
  path?: string;
  excludingAnonymous?: boolean;
};
export function useCompanyEvents({
  company: providedCompany,
  type,
  path,
  excludingAnonymous,
}: UseCompanyEventArguments) {
  const variables: CompanyQueryVariables = {
    first: INITIAL_PAGE_SIZE,
    id: providedCompany.id,
    type: type || undefined,
    excludingAnonymous,
  };

  if (type === 'page') {
    variables.path = path;
  }

  const {
    data = {},
    error,
    loading,
    fetchMore,
    refetch,
    networkStatus,
  } = useQuery(query, {
    fetchPolicy: 'cache-and-network',
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const company = { ...providedCompany, ...data?.company };

  const { events, eventsHasNextPage, eventsEndCursor } = paginateData(company, {
    fields: ['events'],
  });

  const handleFetchMore = () => {
    if (loading) {
      return;
    }

    fetchMore({
      variables: {
        ...variables,
        after: eventsEndCursor,
        first: PAGE_SIZE,
      },

      updateQuery: (prev, { fetchMoreResult }: any) => {
        if (!fetchMoreResult) {
          return prev;
        }

        fetchMoreResult.company.events.edges = [
          ...prev.company.events.edges,
          ...fetchMoreResult.company.events.edges,
        ];

        return fetchMoreResult;
      },
    });
  };

  return {
    error,
    loading,
    loadingFirstPage:
      loading &&
      networkStatus !== NetworkStatus.fetchMore &&
      events.length === 0,
    fetchMore: handleFetchMore,
    refetch,
    data: {
      company,
      events,
      eventsHasNextPage,
      eventsEndCursor,
    },
  };
}
